import { useCallback, useState } from 'react';
import { ScaffolderTask } from '@backstage/plugin-scaffolder-common';

/**
 * Normalizes the system parameter to `namespace/name` format regardless of
 * how it was stored:
 *   - `system:supply-logistica/marketplace` → `supply-logistica/marketplace`
 *   - `supply-logistica/marketplace`        → `supply-logistica/marketplace`
 *   - `supply-logistica-marketplace`        → `supply-logistica-marketplace`
 */
export const normalizeSystem = (value: unknown): string => {
  if (!value) return '-';
  return String(value).replace(/^[a-z]+:/i, '');
};

export interface ActivityFilters {
  dateStart: Date | null;
  dateEnd: Date | null;
  user: string;
  system: string;
  feature: string;
}

const DEFAULT_FILTERS: ActivityFilters = {
  dateStart: null,
  dateEnd: null,
  user: '',
  system: '',
  feature: '',
};

export const useActivityHistoryFilters = () => {
  const [filters, setFilters] = useState<ActivityFilters>(DEFAULT_FILTERS);

  const updateFilter = useCallback(<K extends keyof ActivityFilters>(
    key: K,
    value: ActivityFilters[K],
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const applyFilters = useCallback((tasks: ScaffolderTask[]): ScaffolderTask[] => {
    return tasks.filter(task => {
      // Filter by date range
      if (filters.dateStart) {
        const createdAt = new Date(task.createdAt);
        if (createdAt < filters.dateStart) return false;
      }
      if (filters.dateEnd) {
        const createdAt = new Date(task.createdAt);
        const endOfDay = new Date(filters.dateEnd);
        endOfDay.setHours(23, 59, 59, 999);
        if (createdAt > endOfDay) return false;
      }

      // Filter by user
      if (filters.user && task.spec?.user?.ref) {
        if (!task.spec.user.ref.toLowerCase().includes(filters.user.toLowerCase())) {
          return false;
        }
      }

      // Filter by system (from parameters)
      if (filters.system) {
        const params = task.spec?.parameters as Record<string, unknown> | undefined;
        const raw = params?.system ?? params?.systemEntityRef ?? params?.targetSystem ?? params?.product;
        const system = normalizeSystem(raw);
        if (!system.toLowerCase().includes(filters.system.toLowerCase())) {
          return false;
        }
      }

      // Filter by feature (template)
      if (filters.feature) {
        const templateName = task.spec?.templateInfo?.entity?.metadata?.title
          || task.spec?.templateInfo?.entity?.metadata?.name
          || '';
        if (!String(templateName).toLowerCase().includes(filters.feature.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [filters]);

  return {
    filters,
    updateFilter,
    resetFilters,
    applyFilters,
  };
};
